<template>
    <div>
        <Panel
            v-if="!accessPoint"
            :title="$t('Routing Schedules')"
            :add-button="$store.hasRight('ADD_ROUTING_SCHEDULES') ? $t('New Routing Schedule') : ''"
            :delete-button="selected.length > 0"
            :help-text="$t('DESC_ROUTING_SCHEDULES')"
            :total-rows="items.length"
            :page="page"
            @delete="handleDelete"
            @input="openForm(-1)"
            @change="setPage"
        >
            <table id="routingschedules_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Schedule Name') }}</th>
                        <th v-if="notInUse" style="width: 70px"></th>
                        <th v-if="$store.hasRight('DELETE_ROUTING_SCHEDULES')" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="schedule in schedules" @click="(evt) => handleOpen(schedule.scheduleId, evt)">
                        <td class="primary">
                            {{ schedule.name }}
                        </td>
                        <td v-if="notInUse">
                            <b v-if="!schedule.inUse">{{ $t('Not In Use') }}</b>
                        </td>
                        <td v-if="$store.hasRight('DELETE_ROUTING_SCHEDULES')">
                            <cic-checkbox
                                v-if="!schedule.inUse"
                                :value="selected.indexOf(schedule.scheduleId) !== -1"
                                @click="handleSelected(schedule.scheduleId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>

        <EditRoutingSchedule
            v-if="item !== null && !$store.showLoadingOverlay"
            :access-point="accessPoint"
            :calendars="calendars"
            :plans="plans"
            :schedule="item"
            :schedules="items"
            @assign="saveAssignment"
            @blur="closePopup"
            @calendar="openCalendar"
            @change="updateFieldValue"
            @input="saveSchedule"
            @select="openForm"
        />

        <EditCalendar
            v-if="item && calendar && !$store.showLoadingOverlay"
            :value="calendar"
            :calendars="calendars"
            @blur="calendar = null"
            @change="(val) => (calendar = val)"
            @delete="deleteCalendar"
            @input="saveCalendar"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import moment from 'moment';
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { deleteItems, load, save } from '@/utils/Http';
    import EditCalendar from './EditCalendar';
    import EditRoutingSchedule from './EditRoutingSchedule';
    import './resources/styles/routingSchedules.scss';

    export default {
        name: 'RoutingSchedules',
        components: {
            Panel,
            CicCheckbox,
            EditCalendar,
            EditRoutingSchedule,
        },
        mixins: [TableUtils],
        props: {
            accessPoint: {
                type: Object,
                default: () => null,
            },
        },
        emits: ['blur', 'change'],
        data() {
            return {
                calendar: null,
                calendars: [],
                item: null,
                items: [],
                orgId: null,
                page: 1,
                plans: [],
                selected: [],
            };
        },
        computed: {
            notInUse() {
                return this.schedules.some((x) => !x.inUse);
            },
            schedules() {
                return this.items.slice((this.page - 1) * 20, this.page * 20);
            },
        },
        created() {
            this.orgId = this.accessPoint ? this.accessPoint.orgId : null;
            if (this.accessPoint) {
                this.openForm(this.accessPoint.scheduleId);
            } else {
                this.loadTable();
            }
        },
        methods: {
            closePopup() {
                this.calendar = null;
                this.item = null;
                this.$emit('blur');
            },

            deleteCalendar(calendarId) {
                deleteItems('calendars/delete', { orgId: this.orgId, calendarIds: [calendarId] }, () => {
                    this.loadCalendars();
                });
            },

            handleDelete() {
                deleteItems('schedules/delete', { scheduleIds: this.selected }, () => {
                    this.loadTable();
                });
            },

            loadCalendars(calendarId = null) {
                load('calendars/get', { orgId: this.orgId }, (response) => {
                    this.calendars = response.calendars;
                    this.openCalendar(calendarId);
                });
            },

            loadTable() {
                this.item = null;
                this.selected = [];
                load('schedules/get', {}, (response) => {
                    this.items = response.schedules;
                });
            },

            openCalendar(calendarId = null) {
                if (calendarId) {
                    this.calendar = JSON.parse(JSON.stringify(this.calendars.find((x) => x.calendarId === calendarId)));
                } else {
                    this.calendar = {
                        calendarId: null,
                        name: '',
                        ranges: [
                            {
                                start: moment().format('YYYY-MM-DD'),
                                end: moment().format('YYYY-MM-DD'),
                                description: '',
                            },
                        ],
                    };
                }
            },

            openForm(scheduleId) {
                this.calendar = null;
                this.item = null;
                const planParams = {
                    orgId: this.orgId,
                    scheduleId: scheduleId > 0 ? scheduleId : null,
                    accessId: this.accessPoint ? this.accessPoint.accessId : null,
                    groupId: this.accessPoint ? this.accessPoint.groupId : null,
                };
                load('routeplans/get', planParams, (response) => {
                    this.plans = response.plans;
                });
                load('calendars/get', { orgId: this.orgId }, (response) => {
                    this.calendars = response.calendars;
                });

                if (scheduleId > 0) {
                    load('schedule/get', { orgId: this.orgId, scheduleId }, (response) => {
                        this.item = response.schedule;
                    });
                } else {
                    this.item = {
                        scheduleId: null,
                        name: '',
                        routes: [
                            {
                                start: '00:00:00',
                                end: '00:00:00',
                                mon: false,
                                tue: false,
                                wed: false,
                                thu: false,
                                fri: false,
                                sat: false,
                                sun: false,
                                holiday: false,
                                calendarId: null,
                                planId: null,
                            },
                        ],
                    };
                }

                if (this.accessPoint) {
                    const accessParams = {
                        orgId: this.orgId,
                        selected: scheduleId > 0 ? scheduleId : null,
                        accessId: this.accessPoint.accessId,
                        groupId: this.accessPoint.groupId,
                    };
                    load('schedules/get', accessParams, (response) => {
                        this.items = response.schedules;
                    });
                }
            },

            saveAssignment(scheduleId) {
                save('accesspoint/updateschedule', { ...this.accessPoint, scheduleId }, () => {
                    this.item = null;
                    this.$emit('change');
                });
            },

            saveCalendar() {
                if (this.calendar.name.trim() === '') {
                    alert(this.$t('Calendar Name is a required field'));
                    return;
                }

                const params = {
                    orgId: this.orgId,
                    calendarId: this.calendar.calendarId,
                    name: this.calendar.name,
                    ranges: JSON.stringify(this.calendar.ranges),
                };

                save('calendar/update', params, (response) => {
                    this.loadCalendars(response.calendarId);
                });
            },

            saveSchedule() {
                if (this.item.name.trim() === '') {
                    alert(this.$t('Schedule Name is a required field'));
                    return;
                }

                const params = {
                    orgId: this.orgId,
                    scheduleId: this.item.scheduleId,
                    name: this.item.name,
                    routes: JSON.stringify(this.item.routes),
                };

                save('schedule/update', params, (response) => {
                    if (this.accessPoint) {
                        this.saveAssignment(response.scheduleId);
                    } else {
                        this.loadTable();
                    }
                });
            },
        },
    };
</script>
